import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Chip,
  Input,
  ListSubheader,
} from "@mui/material";
import axios from "axios";
import { getItem, setItem } from "../utils/DataProtection";
import { useDispatch } from "react-redux";
import { config_UPTIME } from "../Constants";

const API_URL = config_UPTIME.url.API_URL + "/userinfo/update";

const PersistedMultiSelect = ({
  userId,
  userNodeId,
  userInfo,
  section,
  i18nLabel = "Site/Area",
  value = [], // Now expects an array
  onChange,
  subscribedSites,
  subscribedEquipmentpools,
  className,
  disabled = false,
  initializeAllIfMissing = false,
}) => {
  const persisted = getItem("persisted_content_configurations");
  const hasInitializedDefaultRef = useRef(false);

  const dispatch = useDispatch();

  // Ensure we always work with an array
  const [internalValue, setInternalValue] = useState(
    Array.isArray(value) ? value : [],
  );
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("info");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");

  const handleChange = (event) => {
    if (disabled) return;
    const {
      target: { value: incomingValue },
    } = event;
    let currentSelection =
      typeof incomingValue === "string"
        ? incomingValue.split(",")
        : incomingValue;

    const added = currentSelection.filter((x) => !internalValue.includes(x));
    const removed = internalValue.filter((x) => !currentSelection.includes(x));

    // --- 1. DOWNWARD SYNC (Parent to Child) ---
    // If 'Alle Pools' is added, select all individual pools
    if (added.includes("p==>0")) {
      const allPoolIds = Object.values(subscribedEquipmentpools).map(
        (p) => `p==>${p.nodeid}`,
      );
      currentSelection = Array.from(
        new Set([...currentSelection, ...allPoolIds]),
      );
    }
    // If 'Alle Pools' is removed, remove all individual pools
    else if (removed.includes("p==>0")) {
      const allPoolIds = Object.values(subscribedEquipmentpools).map(
        (p) => `p==>${p.nodeid}`,
      );
      currentSelection = currentSelection.filter(
        (val) => !allPoolIds.includes(val),
      );
    }

    // If a Site is added, select all its Areas
    if (added.some((x) => x.startsWith("s==>"))) {
      const siteId = added.find((x) => x.startsWith("s==>")).split("==>")[1];
      const areaIds =
        subscribedSites[siteId]?.hasarea?.map((y) => `a==>${y.area.nodeid}`) ||
        [];
      currentSelection = Array.from(new Set([...currentSelection, ...areaIds]));
    }

    // If a Site is removed, remove all its Areas
    if (removed.some((x) => x.startsWith("s==>"))) {
      const siteId = removed.find((x) => x.startsWith("s==>")).split("==>")[1];
      const areaIds =
        subscribedSites[siteId]?.hasarea?.map((y) => `a==>${y.area.nodeid}`) ||
        [];
      currentSelection = currentSelection.filter(
        (val) => !areaIds.includes(val),
      );
    }

    // --- 2. UPWARD SYNC (Child to Parent) ---
    // Check Pools: If all individual pools are present, ensure 'p==>0' is checked
    const allPoolIds = Object.values(subscribedEquipmentpools).map(
      (p) => `p==>${p.nodeid}`,
    );
    const allPoolsSelected =
      allPoolIds.length > 0 &&
      allPoolIds.every((id) => currentSelection.includes(id));
    const anyPoolMissing = allPoolIds.some(
      (id) => !currentSelection.includes(id),
    );

    if (allPoolsSelected && !currentSelection.includes("p==>0")) {
      currentSelection.push("p==>0");
    } else if (anyPoolMissing) {
      currentSelection = currentSelection.filter((val) => val !== "p==>0");
    }

    // Check Sites: If all Areas for a site are present, ensure the Site is checked
    Object.values(subscribedSites).forEach((site) => {
      const siteVal = `s==>${site.nodeid}`;
      const areaVals = site.hasarea?.map((y) => `a==>${y.area.nodeid}`) || [];
      const allAreasSelected =
        areaVals.length > 0 &&
        areaVals.every((area) => currentSelection.includes(area));
      const anyAreaMissing = areaVals.some(
        (area) => !currentSelection.includes(area),
      );

      if (allAreasSelected && !currentSelection.includes(siteVal)) {
        currentSelection.push(siteVal);
      } else if (anyAreaMissing) {
        currentSelection = currentSelection.filter((val) => val !== siteVal);
      }
    });

    setInternalValue(currentSelection);

    const reducedPayload = getReducedPayload(currentSelection);

    saveToBackend(reducedPayload);

    if (onChange) onChange({ target: { value: reducedPayload } });
  };

  const getReducedPayload = (selection) => {
    let reduced = [...selection];

    // If 'Alle Pools' is selected, report only 'p==>0'
    if (reduced.includes("p==>0")) {
      const allPoolIds = Object.values(subscribedEquipmentpools).map(
        (p) => `p==>${p.nodeid}`,
      );
      reduced = reduced.filter(
        (val) => val === "p==>0" || !allPoolIds.includes(val),
      );
    }

    // If Site is selected, remove individual Areas from payload
    const selectedSiteIds = reduced
      .filter((v) => v.startsWith("s==>"))
      .map((v) => v.split("==>")[1]);
    reduced = reduced.filter((val) => {
      if (val.startsWith("a==>")) {
        const areaNodeId = val.split("==>")[1];
        const parentSite = Object.values(subscribedSites).find((s) =>
          s.hasarea?.some((a) => a.area.nodeid === areaNodeId),
        );
        return !(parentSite && selectedSiteIds.includes(parentSite.nodeid));
      }
      return true;
    });

    return reduced;
  };

  const saveToBackend = async (selectedArray) => {
    try {
      // 1. Identify which sites are fully selected (present in the array)
      const selectedSiteIds = selectedArray
        .filter((val) => val.startsWith("s==>"))
        .map((val) => val.split("==>")[1]);

      // 2. Filter the array to send to the API
      const filteredForApi = selectedArray.filter((val) => {
        const [prefix, nodeId] = val.split("==>");

        // If it's an area, check if its parent site is already selected
        if (prefix === "a") {
          const parentSite = Object.values(subscribedSites).find((s) =>
            s.hasarea?.some((area) => area.area.nodeid === nodeId),
          );
          // If parent site is selected, exclude this individual area from the API payload
          if (parentSite && selectedSiteIds.includes(parentSite.nodeid)) {
            return false;
          }
        }

        // If "Alle Equipment Pools" (p==>0) is selected, you might want to exclude individual pools
        if (
          prefix === "p" &&
          nodeId !== "0" &&
          selectedArray.includes("p==>0")
        ) {
          return false;
        }

        return true;
      });

      // 3. Map the filtered list to the required API format
      const options = filteredForApi.map((val) => {
        const [prefix, nodeId] = val.split("==>");
        const name = prefix === "p" ? "pool" : prefix === "a" ? "area" : "site";
        return { name, nodeId };
      });

      const persisted = getItem("persisted_content_configurations");
      let fullConfig = persisted
        ? JSON.parse(persisted)
        : { ...userInfo?.contentconfigurations };

      const updatedUserinfo = { ...(fullConfig || {}) };
      if (!updatedUserinfo.filterSettings) updatedUserinfo.filterSettings = {};
      if (!updatedUserinfo.filterSettings[userNodeId])
        updatedUserinfo.filterSettings[userNodeId] = [];

      const perUserSettings = updatedUserinfo.filterSettings[userNodeId];
      const index = perUserSettings.findIndex(
        (f) => f.section === section && f.type === "Multiselect",
      );

      const newEntry = { section, type: "Multiselect", options };
      if (index > -1) perUserSettings[index] = newEntry;
      else perUserSettings.push(newEntry);

      const user_token = getItem("user");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + JSON.parse(user_token),
        },
      };

      const response = await axios.post(
        API_URL,
        {
          userid: userId,
          userinfo: { contentConfigurations: updatedUserinfo },
        },
        config,
      );

      if (response.status === 200) {
        // setAlertTitle(section + " Filter");
        // setAlertDescription("Gespeichert.");
        // setAlertType("success");
        // setShowAlert(true);

        dispatch({
          type: "SHOW_SNACKBAR",
          payload: {
            open: true,
            title: "Erfolg",
            description: "Filter gespeichert",
            severity: "success",
          },
        });
      }

      setItem(
        "persisted_content_configurations",
        JSON.stringify(updatedUserinfo),
      );

      setItem("userinfo", JSON.stringify(updatedUserinfo));
    } catch (err) {
      dispatch({
        type: "SHOW_SNACKBAR",
        payload: {
          open: true,
          title: "Fahler",
          description: "",
          severity: "error",
        },
      });

      console.error("Save failed", err);
    }
  };

  useEffect(() => {
    let _userInfo = userInfo;

    if (persisted) {
      try {
        const parsed = JSON.parse(persisted);
        _userInfo = { ..._userInfo, contentconfigurations: parsed };
      } catch (err) {
        console.warn("Invalid persisted config:", err);
      }
    }

    const filterList =
      _userInfo?.contentconfigurations?.filterSettings?.[userNodeId] || [];
    const found = filterList.find(
      (f) => f.section === section && f.type === "Multiselect",
    );

    const hasUserFilterSettings =
      _userInfo?.contentconfigurations?.filterSettings?.[userNodeId] !==
      undefined;

    if (found?.options?.length > 0) {
      let savedValues = found.options.map((opt) => {
        const prefix =
          opt.name === "pool"
            ? "p"
            : opt.name === "site"
            ? "s"
            : opt.name === "area"
            ? "a"
            : "";
        return `${prefix}==>${opt.nodeId}`;
      });

      // --- EXPANSION LOGIC FOR INITIAL LOAD ---
      let expandedValues = [...savedValues];

      // 1. If "Alle Equipment Pools" is in saved data, add all individual pool IDs
      if (savedValues.includes("p==>0") && subscribedEquipmentpools) {
        const allPoolIds = Object.values(subscribedEquipmentpools).map(
          (p) => `p==>${p.nodeid}`,
        );
        expandedValues = Array.from(
          new Set([...expandedValues, ...allPoolIds]),
        );
      }

      // 2. If any Site is in saved data, add all its Area IDs
      savedValues.forEach((val) => {
        if (val.startsWith("s==>") && subscribedSites) {
          const siteId = val.split("==>")[1];
          const site = Object.values(subscribedSites).find(
            (s) => s.nodeid === siteId,
          );
          if (site?.hasarea) {
            const areaIds = site.hasarea.map((y) => `a==>${y.area.nodeid}`);
            expandedValues = Array.from(
              new Set([...expandedValues, ...areaIds]),
            );
          }
        }
      });

      setTimeout(() => {
        setInternalValue(expandedValues);
        if (onChange) {
          onChange({ target: { value: expandedValues } });
        }
      }, 1000);
      return;
    }

    if (
      initializeAllIfMissing &&
      !hasInitializedDefaultRef.current &&
      !hasUserFilterSettings &&
      subscribedSites &&
      subscribedEquipmentpools
    ) {
      const siteValues = Object.values(subscribedSites).flatMap((site) => {
        const siteId = `s==>${site.nodeid}`;
        const areaIds =
          site.hasarea?.map((areaEntry) => `a==>${areaEntry.area.nodeid}`) || [];
        return [siteId, ...areaIds];
      });

      const poolValues = Object.values(subscribedEquipmentpools).map(
        (pool) => `p==>${pool.nodeid}`,
      );

      const defaultValues = [
        ...siteValues,
        ...(poolValues.length ? ["p==>0", ...poolValues] : []),
      ];

      const expandedDefaults = Array.from(new Set(defaultValues));
      const reducedPayload = getReducedPayload(expandedDefaults);

      hasInitializedDefaultRef.current = true;
      setInternalValue(expandedDefaults);
      saveToBackend(reducedPayload);

      if (onChange) {
        onChange({ target: { value: expandedDefaults } });
      }
    }
  }, [subscribedSites, subscribedEquipmentpools]); // Dependency ensures it runs once data is available

  return (
    <>
      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }} // Slide in from bottom-left
      >
        <Alert severity={alertType}>
          {alertTitle}: {alertDescription}
        </Alert>
      </Snackbar>

      <FormControl
        variant="standard" // 👈 Changed to match Time select
        className={className}
        sx={{ minWidth: 220 }}
        disabled={disabled}
      >
        <InputLabel
          style={{
            color: "#9E9E9E",
            display: "block",
            fontSize: "12px",
            lineHeight: 1,
            marginBottom: "8px",
          }}
          id="persisted-multiselect-label"
        >
          {i18nLabel}
        </InputLabel>
        <Select
          labelId="persisted-multiselect-label"
          multiple
          disabled={disabled}
          value={internalValue}
          onChange={handleChange}
          // Use the standard Input instead of OutlinedInput
          input={<Input />}
          renderValue={(selected) => `${selected.length} Ausgewählt`}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 400,
                backgroundColor: "#ffffff",
                // This targets the specific items in the dropdown
                "& .MuiMenuItem-root": {
                  color: "#000000", // Force black text for all items
                  "&.Mui-selected": {
                    backgroundColor: "rgba(14, 63, 81, 0.1) !important", // Soft version of your teal
                    color: "#000000 !important", // Keep text black even when selected
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "rgba(14, 63, 81, 0.2) !important",
                  },
                  // Fix the checkbox color so it doesn't turn white
                  "& .MuiCheckbox-root": {
                    color: "#0e3f51",
                  },
                },
              },
            },
          }}
        >
          <ListSubheader
            sx={{
              backgroundColor: "#f8f9fa",
              fontWeight: "bold",
              color: "#000",
            }}
          >
            Werke & Bereiche
          </ListSubheader>

          {Object.keys(subscribedSites).length === 0 && (
            <MenuItem disabled sx={{ pl: 4 }}>
              keine Werke & Bereiche
            </MenuItem>
          )}

          {subscribedSites &&
            Object.keys(subscribedSites).map((key) => {
              const site = subscribedSites[key];
              return [
                // Parent Site
                <MenuItem
                  key={`s-${site.nodeid}`}
                  value={`s==>${site.nodeid}`}
                  sx={{ fontWeight: "bolder" }}
                >
                  <Checkbox
                    checked={internalValue.includes(`s==>${site.nodeid}`)}
                  />
                  <ListItemText primary={`Alle ${site.name}`} />
                </MenuItem>,
                // Child Areas
                ...(site.hasarea || []).map((y) => (
                  <MenuItem
                    key={`a-${y.area.nodeid}`}
                    value={`a==>${y.area.nodeid}`}
                    sx={{ pl: 4 }} // Indentation for hierarchy
                  >
                    <Checkbox
                      size="small"
                      checked={internalValue.includes(`a==>${y.area.nodeid}`)}
                    />
                    <ListItemText
                      primaryTypographyProps={{ fontSize: "0.9rem" }}
                      primary={y.area.name}
                    />
                  </MenuItem>
                )),
              ];
            })}

          {/* Equipment Pools Section */}

          <ListSubheader
            sx={{
              backgroundColor: "#f8f9fa",
              fontWeight: "bold",
              color: "#000",
            }}
          >
            Equipment Pools
          </ListSubheader>

          {Object.keys(subscribedEquipmentpools).length === 0 && (
            <MenuItem disabled sx={{ pl: 4 }}>
              keine Equipment Pools
            </MenuItem>
          )}

          {Object.keys(subscribedEquipmentpools).length && (
            <MenuItem value="p==>0">
              <Checkbox checked={internalValue.includes("p==>0")} />
              <ListItemText primary="Alle Equipment Pools" />
            </MenuItem>
          )}
          {subscribedEquipmentpools &&
            Object.keys(subscribedEquipmentpools).map((key) => (
              <MenuItem
                key={`p-${key}`}
                value={`p==>${subscribedEquipmentpools[key].nodeid}`}
                sx={{ pl: 4 }}
              >
                <Checkbox
                  size="small"
                  checked={internalValue.includes(
                    `p==>${subscribedEquipmentpools[key].nodeid}`,
                  )}
                />
                <ListItemText
                  primaryTypographyProps={{ fontSize: "0.9rem" }}
                  primary={subscribedEquipmentpools[key].name}
                />
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </>
  );
};

export default PersistedMultiSelect;
